/**
 * Theme Service
 * Handles domain detection and theme selection
 */

import { themes } from '../themes/themeConfig';

/**
 * Domain to Theme mapping
 */
const DOMAIN_THEME_MAP = {
  paypaygateway: 'themeA',
  runpayrun: 'themeB',
};

/**
 * Detect the current domain from window.location
 * @returns {string} The detected domain name (without TLD)
 */
export const detectDomain = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  const hostname = window.location.hostname.toLowerCase();

  // Extract the main domain name from the hostname
  // Handles cases like:
  // - paypaygateway.com → paypaygateway
  // - www.paypaygateway.com → paypaygateway
  // - runpayrun.io → runpayrun
  // - dev.runpayrun.com → runpayrun
  // - localhost → localhost
  
  // Remove 'www.' prefix if present
  const cleanHostname = hostname.replace(/^www\./, '');
  
  // Check for exact matches or partial matches in the domain
  for (const [domainKey] of Object.entries(DOMAIN_THEME_MAP)) {
    if (cleanHostname.includes(domainKey)) {
      return domainKey;
    }
  }
  
  // Return the hostname as-is if no match found (for localhost, etc.)
  return cleanHostname.split('.')[0];
};

/**
 * Get the theme configuration for a given domain
 * @param {string} domain - Domain name (optional, auto-detects if not provided)
 * @returns {Object} Theme configuration object
 */
export const getThemeForDomain = (domain = null) => {
  const detectedDomain = domain || detectDomain();
  
  // Map domain to theme key
  const themeKey = DOMAIN_THEME_MAP[detectedDomain];
  
  // Return the theme or default to Theme A
  if (themeKey && themes[themeKey]) {
    return themes[themeKey];
  }
  
  // Fallback to Theme B for unrecognized domains
  console.warn(`Domain "${detectedDomain}" not recognized. Falling back to Theme A.`);
  return themes.themeB;
};

/**
 * Get the theme key for a given domain
 * @param {string} domain - Domain name (optional, auto-detects if not provided)
 * @returns {string} Theme key ('themeA' or 'themeB')
 */
export const getThemeKey = (domain = null) => {
  const detectedDomain = domain || detectDomain();
  return DOMAIN_THEME_MAP[detectedDomain] || 'themeB';
};

/**
 * Check if a domain is configured
 * @param {string} domain - Domain name
 * @returns {boolean} True if domain has a theme configured
 */
export const isDomainConfigured = (domain) => {
  return domain in DOMAIN_THEME_MAP;
};

export default {
  detectDomain,
  getThemeForDomain,
  getThemeKey,
  isDomainConfigured,
};
